import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { TEMPLATES } from "@/components/templates";
import { ModeTabs } from "@/components/PromptBox";
import { FranchiseFields } from "@/components/FranchiseFields";
import { FranchiseTemplatePicker } from "@/components/FranchiseTemplatePicker";
import { figuresToForm } from "@/components/franchiseTemplates";
import { SiteMap } from "@/components/SiteMap";
import { useClients, ClientSelect } from "@/components/ClientSelect";

const EMPTY = { client_id: "", project_name: "", description: "", address: "", property_type: "", industry: "", location: "", budget: "", currency: "USD", timeline_months: "", target_market: "", franchise_brand: "", franchise_category: "", franchise_fee: "", royalty_pct: "", marketing_pct: "", investment_min: "", investment_max: "", system_units: "", fdd_id: "" };
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED", "SAR", "NGN", "CAD", "AUD"];

const F = ({ label, children }) => <div className="space-y-2"><Label className="text-xs uppercase tracking-wider text-slate-400">{label}</Label>{children}</div>;

export default function NewStudy() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("idea");
  const [form, setForm] = useState(EMPTY);
  const [mapAddress, setMapAddress] = useState("");
  const [tpl, setTpl] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const clients = useClients();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const field = "bg-slate-950/60 border-slate-700 focus-visible:ring-emerald-500";
  const isAddr = mode === "address";
  const isFr = mode === "franchise";

  useEffect(() => {
    const t = TEMPLATES.find((x) => x.id === params.get("template"));
    if (t) { setMode(t.mode); setForm({ ...EMPTY, ...t.form }); setTpl(t.id); }
  }, [params]);
  useEffect(() => { const t = setTimeout(() => setMapAddress(form.address), 900); return () => clearTimeout(t); }, [form.address]);

  const pickTemplate = (t) => { setTpl(t.id); setForm((f) => ({ ...f, ...t.form })); };
  const onFdd = (d) => setForm((f) => ({ ...f, ...figuresToForm(d.figures || {}), franchise_brand: f.franchise_brand || d.figures?.brand || "", fdd_id: d.id }));
  const onFddClear = () => setForm((f) => ({ ...f, fdd_id: "" }));

  const submit = async (e) => {
    e.preventDefault();
    if (isFr && !form.franchise_brand.trim()) return toast.error("Franchise brand is required");
    if (isAddr && !form.address.trim()) return toast.error("Property address is required");
    if (!isFr && !form.description.trim()) return toast.error(isAddr ? "Describe what you plan at this address" : "Project description is required");
    if (mode === "idea" && !form.project_name.trim()) return toast.error("Project name is required");
    setLoading(true);
    try {
      const payload = { ...form, client_id: form.client_id || null, mode, budget: form.budget ? Number(form.budget) : null, timeline_months: form.timeline_months ? Number(form.timeline_months) : null };
      ["franchise_fee", "royalty_pct", "marketing_pct", "investment_min", "investment_max", "system_units"].forEach((k) => { payload[k] = isFr && form[k] !== "" ? Number(form[k]) : null; });
      if (!isFr) { payload.franchise_brand = ""; payload.franchise_category = ""; payload.fdd_id = ""; }
      if (isFr && !payload.project_name.trim()) payload.project_name = `${form.franchise_brand.trim()} franchise${form.address ? ` – ${form.address.split(",")[0]}` : ""}`;
      if (mode === "idea") { payload.address = ""; payload.property_type = ""; }
      const r = await api.post("/studies", payload);
      navigate(`/studies/${r.data.id}`);
    } catch {
      toast.error("Could not start generation");
      setLoading(false);
    }
  };

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">New feasibility study</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">{isFr ? "Evaluate a franchise." : isAddr ? "Analyze a site." : "Describe your project."}</h1>
        <p className="text-slate-400 mb-8">{isAddr ? "Enter the property address and what you plan there. You'll get a site & location analysis on top of the full study." : "The AI drafts every section. You review, edit and rewrite anything afterwards."}</p>
        <div className="mb-8"><ModeTabs mode={mode} setMode={setMode} franchise /></div>
        <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-slate-900/80 backdrop-blur-md p-6 md:p-8 space-y-6" data-testid="new-study-form">
          <F label="Client (who is this study for?)"><ClientSelect value={form.client_id} onChange={(v) => setForm((f) => ({ ...f, client_id: v }))} clients={clients} testId="new-study-client" /></F>
          {isFr && <FranchiseTemplatePicker selected={tpl} onPick={pickTemplate} onBrand={(b) => setForm((f) => ({ ...f, franchise_brand: b }))} />}
          {isFr && <FranchiseFields form={form} set={set} field={field} onFdd={onFdd} onFddClear={onFddClear} />}
          {(isAddr || isFr) && (
            <>
              <F label={isFr ? "Property address (optional, adds live site data)" : "Property address *"}><Input data-testid="input-address" value={form.address} onChange={set("address")} className={field} placeholder="e.g. 1100 S Congress Ave, Austin, TX 78704" /></F>
              <SiteMap address={mapAddress} height={220} testId="new-study-map" />
              <F label="Property type"><Input data-testid="input-property-type" value={form.property_type} onChange={set("property_type")} className={field} placeholder="e.g. Vacant lot, retail unit, warehouse, house" /></F>
            </>
          )}
          <F label={mode !== "idea" ? "Project name (optional — AI will suggest one)" : "Project name *"}><Input data-testid="input-project-name" value={form.project_name} onChange={set("project_name")} className={field} placeholder="e.g. Solar-powered car wash" /></F>
          <F label={isFr ? "Notes (optional): your situation, goals, concerns" : isAddr ? "What do you plan at this address? *" : "Description *"}><Textarea data-testid="input-description" rows={4} value={form.description} onChange={set("description")} className={field} placeholder={isAddr ? "e.g. Drive-thru coffee shop with 20 parking spaces" : "What will the business do, how will it make money?"} /></F>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <F label="Industry"><Input data-testid="input-industry" value={form.industry} onChange={set("industry")} className={field} placeholder="e.g. Retail" /></F>
            {!isAddr && <F label="Location / region"><Input data-testid="input-location" value={form.location} onChange={set("location")} className={field} placeholder="e.g. Lagos, Nigeria" /></F>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            <F label="Budget"><Input data-testid="input-budget" type="number" value={form.budget} onChange={set("budget")} className={`${field} num`} placeholder="250000" /></F>
            <F label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger data-testid="select-currency" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">{CURRENCIES.map((c) => <SelectItem key={c} value={c} data-testid={`currency-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Timeline (months)"><Input data-testid="input-timeline" type="number" value={form.timeline_months} onChange={set("timeline_months")} className={`${field} num`} placeholder="12" /></F>
          </div>
          <F label="Target market"><Input data-testid="input-target-market" value={form.target_market} onChange={set("target_market")} className={field} placeholder="Who are your customers?" /></F>
          <Button type="submit" data-testid="generate-study-btn" disabled={loading} className="w-full h-12 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 font-semibold">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Generate feasibility study
          </Button>
        </form>
      </div>
    </div>
  );
}
